import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import {
    AppBarContainer,
    AtTheTop,
    AtTheBottom,
    AppBarIconLink,
    AppBarIconExternalLink,
    AppBarPartnerLink,
    AppBarDinteroLogo,
    AppBarMerchantLogo,
    BurgerButton
} from './components/AppBar';
import { Allow } from './auth/accessToken';
import LeaveInspectAppBarButton from './partner/LeaveInspectAppBarButton';
import { useAccountId } from './auth/useAccountId';
import { getCurrentLanguage } from './i18n';

const backofficeHelpUrl = (language: string) => {
    if (['no', 'se'].includes(language)) {
        return `https://www.dintero.com/${language}/support`;
    }
    return 'https://www.dintero.com/support';
};

const AppBarInternal = ({ t, accountId, language }: { t: TFunction; accountId: string; language: string }) => {
    return (
        <AppBarContainer>
            <AppBarMerchantLogo icon="profile" />
            <AtTheTop>
                <AppBarIconLink to={`/${accountId}`} icon="backoffice" title={t('app_bar.titles.dashboard')} exact />
                <Allow
                    anyOfScopes={[['admin'], ['admin:orders'], ['read:orders'], ['admin:shopping'], ['read:shopping']]}
                >
                    <AppBarIconLink to={`/${accountId}/orders`} icon="orders" title={t('app_bar.titles.orders')} />
                </Allow>
                <Allow anyOfScopes={[['admin'], ['admin:checkout'], ['read:checkout']]}>
                    <AppBarIconLink
                        to={`/${accountId}/payments`}
                        icon="payments"
                        title={t('app_bar.titles.payments')}
                    />
                </Allow>
                <Allow anyOfScopes={[['admin'], ['admin:accounts'], ['admin:reports'], ['read:reports']]}>
                    <AppBarIconLink to={`/${accountId}/reports`} icon="reports" title={t('app_bar.titles.reports')} />
                </Allow>
                <Allow anyOfScopes={[['admin'], ['admin:customers'], ['read:customers']]}>
                    <AppBarIconLink
                        to={`/${accountId}/customers`}
                        icon="customers"
                        title={t('app_bar.titles.customers')}
                        className="hideMobile"
                    />
                </Allow>
                <Allow anyOfScopes={[['admin'], ['admin:discounts'], ['read:discounts']]}>
                    <AppBarIconLink
                        to={`/${accountId}/marketing/discounts`}
                        icon="campaigns"
                        title={t('app_bar.titles.marketing')}
                    />
                </Allow>
                <Allow
                    anyOfScopes={[['read:payout'], ['admin:payout'], ['read:payout:/admin'], ['admin:payout:/admin']]}
                    // Temporary just to hide the menu option. (scope takes precedence over anyOfScopes, ie overrides)
                    scope="admin:dintero"
                >
                    <AppBarIconLink
                        to={`/${accountId}/payout`}
                        icon="payments" // TODO: Icon
                        title={t('app_bar.titles.payout', 'Payout')}
                    />
                </Allow>
                <Allow anyOfScopes={[['admin'], ['admin:accounts']]}>
                    <AppBarIconLink
                        to={`/${accountId}/settings`}
                        icon="settings"
                        title={t('app_bar.titles.settings')}
                    />
                </Allow>
                <AppBarPartnerLink accountId={accountId} />
                <LeaveInspectAppBarButton />
            </AtTheTop>
            <AtTheBottom>
                <AppBarIconExternalLink
                    icon="docs"
                    title={t('app_bar.titles.docs')}
                    href="https://docs.dintero.com"
                />

                <AppBarIconExternalLink
                    icon="help"
                    title={t('app_bar.titles.help')}
                    href={backofficeHelpUrl(language)}
                />
                <AppBarDinteroLogo />
            </AtTheBottom>
            <BurgerButton />
        </AppBarContainer>
    );
};

const AppBarInternalMemo = memo(AppBarInternal);

const AppBar = () => {
    const { t, i18n } = useTranslation();
    const accountId = useAccountId();
    const language = getCurrentLanguage(i18n);
    return <AppBarInternalMemo t={t} accountId={accountId} language={language} />;
};

export default AppBar;
